import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import { Gift, Coins, Sparkles, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = { pending: "badge badge-pending", fulfilled: "badge badge-approved", rejected: "badge badge-destructive" };
const ITEM_EMOJI = (name) => /leave/i.test(name) ? "🏖️" : /half.?day/i.test(name) ? "⏰" : /mug/i.test(name) ? "☕" : /shirt/i.test(name) ? "👕" : /massage/i.test(name) ? "💆" : /coffee/i.test(name) ? "🎫" : "🎁";

export default function RewardsExchange() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState("");

    const load = () => api.get("/rewards/exchange").then((r) => setData(r.data)).catch((e) => {
        toast.error(formatErrorDetail(e.response?.data?.detail) || "Could not load the exchange");
    });
    useEffect(() => { load(); }, []);

    const redeem = async (item) => {
        if (!window.confirm(`Redeem “${item.name}” for ${item.points_cost} points?`)) return;
        setBusy(item.id);
        try {
            await api.post("/rewards/exchange/redeem", { item_id: item.id });
            toast.success(`Redeemed! HR will process your “${item.name}”.`);
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Redeem failed"); }
        setBusy("");
    };

    if (!data) return <div className="text-sm text-muted-foreground">Loading exchange…</div>;

    return (
        <div>
            <PageHeader title="Rewards Exchange" subtitle="Trade your recognition points for real rewards. HR fulfils redemptions."
                actions={<button className="h-9 px-3 rounded-md border border-border text-sm flex items-center gap-1" onClick={() => navigate("/rewards")}><ArrowLeft className="size-4" /> Back to Rewards</button>} />

            <div className="grid gap-3 md:grid-cols-4 mb-4">
                <Surface className="p-4 bg-primary text-white">
                    <div className="text-xs uppercase tracking-wide opacity-80 flex items-center gap-1"><Coins className="size-3" /> My balance</div>
                    <div className="text-3xl font-bold font-mono" data-testid="points-balance">{data.balance}</div>
                    <div className="text-[11px] opacity-80">points to spend</div>
                </Surface>
                <Surface className="p-4"><div className="text-xs uppercase text-muted-foreground">From kudos</div><div className="text-xl font-bold font-mono">{data.earned_kudos}</div></Surface>
                <Surface className="p-4"><div className="text-xs uppercase text-muted-foreground">From activities</div><div className="text-xl font-bold font-mono">{data.earned_activities}</div></Surface>
                <Surface className="p-4"><div className="text-xs uppercase text-muted-foreground">Spent</div><div className="text-xl font-bold font-mono">{data.spent}</div></Surface>
            </div>

            <h3 className="font-display font-semibold mb-2 flex items-center gap-2"><Gift className="size-4 text-primary" /> Reward catalog</h3>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3 mb-5">
                {data.items.map((it) => {
                    const afford = data.balance >= it.points_cost;
                    return (
                        <Surface key={it.id} className="p-4 flex items-center justify-between gap-3" data-testid={`item-${it.id}`}>
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="text-2xl">{ITEM_EMOJI(it.name)}</div>
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold truncate">{it.name}</div>
                                    {it.description && <div className="text-[11px] text-muted-foreground truncate">{it.description}</div>}
                                    <div className="text-xs font-mono font-bold text-primary">{it.points_cost} pts</div>
                                </div>
                            </div>
                            <button disabled={!afford || busy === it.id} onClick={() => redeem(it)}
                                className={`h-8 px-3 rounded-md text-xs font-medium shrink-0 ${afford ? "bg-primary text-white" : "border border-border text-muted-foreground cursor-not-allowed"}`}
                                data-testid={`redeem-${it.id}`}>
                                {busy === it.id ? "…" : afford ? "Redeem" : "Not enough"}
                            </button>
                        </Surface>
                    );
                })}
                {!data.items.length && <Surface className="p-5 md:col-span-3"><Empty title="No rewards yet" subtitle="HR hasn't added catalog items." /></Surface>}
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                <div>
                    <h3 className="font-display font-semibold mb-2 flex items-center gap-2"><Sparkles className="size-4 text-amber-500" /> How to earn points</h3>
                    <Surface className="p-3 space-y-2">
                        {data.activities.map((a) => (
                            <div key={a.id} className="flex items-center justify-between border-b border-border/60 last:border-0 pb-2 last:pb-0">
                                <div>
                                    <div className="text-sm font-medium">{a.name}</div>
                                    {a.description && <div className="text-[11px] text-muted-foreground">{a.description}</div>}
                                </div>
                                <span className="badge badge-approved font-mono shrink-0">+{a.points} pts</span>
                            </div>
                        ))}
                        <div className="text-[11px] text-muted-foreground pt-1">Plus peer kudos: teamwork +20, excellence +30, above &amp; beyond +50, and more — HR awards activity points.</div>
                    </Surface>
                </div>
                <div>
                    <h3 className="font-display font-semibold mb-2">My redemptions</h3>
                    {!data.my_redemptions.length && <Surface className="p-4"><Empty title="Nothing redeemed yet" /></Surface>}
                    {data.my_redemptions.length > 0 && (
                        <TableWrap>
                            <thead><tr><th>Reward</th><th className="num">Points</th><th>When</th><th>Status</th></tr></thead>
                            <tbody>
                                {data.my_redemptions.map((r) => (
                                    <tr key={r.id}>
                                        <td className="text-sm">{ITEM_EMOJI(r.item_name)} {r.item_name}</td>
                                        <td className="num font-mono">−{r.points_cost}</td>
                                        <td className="text-xs">{formatDateTime(r.created_at)}</td>
                                        <td>
                                            <span className={STATUS_BADGE[r.status] || "badge"}>{r.status}</span>
                                            {r.status === "fulfilled" && <span className="text-[10px] text-emerald-600 block"><CheckCircle2 className="size-2.5 inline" /> claim from HR</span>}
                                            {r.status === "rejected" && r.note && <span className="text-[10px] text-muted-foreground block">“{r.note}”</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </TableWrap>
                    )}
                </div>
            </div>
        </div>
    );
}
